import { APP_I18N } from '@/client/confs/app';
import { detectLocale, sanitizeLocale } from '@/client/confs/locales';
import * as dom from '@/client/utils/dom';
import { SupportedLanguage } from '@/libs/sdk/api';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { persist } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';
import { createWithEqualityFn } from 'zustand/traditional';

// save key
export const LocaleSaveKey = 'app-locale';

export type ILocale = { name: string; lang: string };
export type TDirection = 'ltr' | 'rtl';
export interface ILocaleState {
  locale: string;
  direction: TDirection;
  setDirection: (newDirection: TDirection) => void;
  setLocale: (newLocale: string) => void;
}
// store
export const useLocaleStore = createWithEqualityFn<ILocaleState>()(
  persist(
    (set, get) => ({
      locale: '',
      direction: 'ltr',
      setDirection(newDirection: TDirection) {
        const { direction } = get();
        if (direction !== newDirection) set({ direction: newDirection });
      },
      setLocale(newLocale: string) {
        const { locale } = get();
        const result = locale !== newLocale ? APP_I18N.locales.find(item => item.lang === newLocale) : null;
        if (result) set({ locale: result.lang });
      },
    }),
    {
      name: LocaleSaveKey,
      // A locale saved before the tenant narrowed VITE_LOCALES (e.g. `ja`)
      // rehydrates as the default instead of an unsupported language.
      merge: (persisted, current) => {
        const state = { ...current, ...(persisted as Partial<ILocaleState>) };
        return { ...state, locale: sanitizeLocale(state.locale || '', APP_I18N.locales, APP_I18N.defaultLocale) };
      },
    }
  ),
  shallow
);

// Re-exported for existing callers/tests; the pure logic lives in confs/locales.
export { detectLocale };

export type useLocaleProps = Record<string, never>;

const useLocale = () => {
  const { locale, setLocale: storeSetLocale, direction, setDirection } = useLocaleStore(state => state);
  const { i18n, ready } = useTranslation();

  useEffect(() => {
    const lng = dom.getLanguage()?.toLowerCase();
    if (!locale && lng) storeSetLocale(detectLocale(lng, APP_I18N.locales, APP_I18N.defaultLocale));
  }, [locale, storeSetLocale]);

  useEffect(() => {
    if (ready && i18n.language !== locale) i18n.changeLanguage(locale);
    dom.setLocale(locale, direction);
  }, [ready, i18n, locale, direction]);

  return {
    locale: locale as SupportedLanguage,
    locales: APP_I18N.locales,
    defaultLocale: APP_I18N.defaultLocale,
    switchLocale: storeSetLocale,
    switchDirection: setDirection,
  };
};

export default useLocale;
